import { ReactNode } from 'react';
import SplitView from '../splitView';
import TableActionButton from '../buttons/tableActionButton';
import ProvenancePdfPanel from '../provenancePdfPanel';
import AuthService from '../../service/auth';
import { ROLES } from '../../constnats/user';
import { FieldProvenanceType } from '../../schemas/report.schemas';
import './sourceView.css';

// Source view lets an employee split a financial table and inspect where each value was extracted
// from. The same toggle, availability gate, and split-layout are shared by every table that opts in.

// Source view is an internal employee feature. Other roles, including admin or beta-feature tester,
// only qualify when the user also has the employee role.
export function isSourceViewAvailable(): boolean {
    return AuthService.hasAnyGlobalRole([ROLES.EMPLOYEE]);
}

type SourceViewTogglePropsType = {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
    className?: string;
};

// A checkbox-style button: one control that reads as checked or unchecked, rather than a two-segment
// toggle. `aria-pressed` is the toggle-button ARIA pattern, and `type` keeps it inert inside a form.
export function SourceViewToggle({ enabled, onChange, className }: SourceViewTogglePropsType) {
    return (
        <TableActionButton
            type="button"
            aria-pressed={enabled}
            className={`source-view-toggle ${enabled ? 'source-view-toggle--checked' : ''} ${className || ''}`}
            onClick={() => onChange(!enabled)}
        >
            Source view
        </TableActionButton>
    );
}

type ProvenanceSplitLayoutPropsType = {
    enabled: boolean;
    provenance: FieldProvenanceType[] | null;
    children: ReactNode;
    // True while the reports are being re-fetched with their provenance. The tables load without it
    // and only ask for it when a user opens Source view, so the first switch-on has a wait.
    loading?: boolean;
    containerRef?: React.RefObject<HTMLElement | null>;
    topOffset?: number;
    // See SplitView: the financial tables need it for their sticky horizontal scrollbar, the review
    // keeps the library defaults.
    leftPaneOverflowVisible?: boolean;
};

// Renders the table alone when source view is off, and side-by-side with the provenance panel when on.
export function ProvenanceSplitLayout({
    enabled,
    provenance,
    children,
    loading,
    containerRef,
    topOffset,
    leftPaneOverflowVisible = false,
}: ProvenanceSplitLayoutPropsType) {
    if (!enabled) {
        return <>{children}</>;
    }

    return (
        <SplitView
            containerRef={containerRef}
            topOffset={topOffset}
            leftPaneOverflowVisible={leftPaneOverflowVisible}
            left={children}
            right={<ProvenancePdfPanel provenance={provenance} loading={loading} />}
        />
    );
}
